import { Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Divider,
    FormControl,
    FormControlLabel,
    FormGroup,
    IconButton,
    Radio,
    RadioGroup,
    Skeleton,
    Stack,
    type SxProps,
    TextField,
    Typography,
} from '@mui/material';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import Chip from '/@/components/bayer/chip';
import BayerModal from '/@/components/bayer/modal';
import colors from '/@/components/theme-provider/colors';
import useUnitProvider from '/@/core/context/unit';
import settings from '/@/core/settings';
import { useAllCatalogProducts, useCatalogProductsById } from '/@/hooks/catalog';
import { useToggle } from '/@/hooks/toggle/useToggle';
import { _ } from '/@/locale';
import { useCatalogZones } from '/@/store/catalog/apiProviders';
import { getSessionCountry } from '/@/store/session/selectors';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import { reverse } from '/@/utils/router';
import type { useProductComparator } from '../../hooks/product-comparator';
import { parseProductForProductCard } from '../product-card';

export const ProductSelectorAutocomplete = ({
    setPreSelectedProducts,
    filteredProducts,
    preSelectedProducts,
    preSelectionMaxSize,
}: {
    setPreSelectedProducts: (ids: Set<number>) => void;
    filteredProducts: any[];
    preSelectedProducts: Set<number>;
    preSelectionMaxSize: number;
}) => (
    <Autocomplete
        multiple
        limitTags={preSelectedProducts.size}
        options={filteredProducts || []}
        disabled={preSelectedProducts.size === preSelectionMaxSize}
        value={filteredProducts.filter((p) => preSelectedProducts.has(p.id))}
        getOptionLabel={(option) => option?.catalogName}
        onChange={(event, newValue) => {
            const newSet = new Set([...preSelectedProducts, ...newValue.map((p) => p?.id)]);
            if (newSet.size <= preSelectedProducts.size || newSet.size <= preSelectionMaxSize) {
                setPreSelectedProducts(newSet);
            }
        }}
        renderTags={() => null}
        disableCloseOnSelect
        renderOption={(props, option) => (
            <Box {...props} disabled={preSelectedProducts.size === preSelectionMaxSize}>
                <Stack direction='row' spacing={1} alignItems='center'>
                    <Typography variant='subtitle2'>{option.catalogName || option.name}</Typography>
                    {option.chips}
                </Stack>
            </Box>
        )}
        renderInput={(params) => (
            <TextField
                {...params}
                variant='outlined'
                placeholder={_('Busca por nombre de {{product}}', {
                    product: _('híbrido'),
                })}
                InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                        <>
                            {params.InputProps.startAdornment}
                            <SearchIcon sx={{ ml: 1 }} />
                        </>
                    ),
                }}
            />
        )}
    />
);

export const ProductCard = ({
    id,
    catalogName,
    name,
    selectProduct,
    typeLabel,
    chips,
    sx,
    catalogType,
    slugLabel,
}: {
    id: number;
    catalogName: string;
    name: string;
    description: string;
    selectProduct: ReturnType<typeof useProductComparator>['selectProduct'];
    typeLabel: string;
    chips?: React.ReactNode[];
    sx?: SxProps;
    catalogType: string;
    slugLabel: string;
}) => {
    const navigate = useNavigate();

    return (
        <Stack
            direction='column'
            sx={{
                position: 'relative',
                background: 'white',
                color: 'black',
                width: '200px',
                ...sx,
            }}
            justifyContent='space-between'
        >
            <Stack spacing={0.5} py={1} px={2} alignItems='start' textAlign='left'>
                <Stack direction='row' justifyContent='space-between' alignItems='center'>
                    <Typography variant='h6' pt={1}>
                        {catalogName || name}
                    </Typography>
                    {selectProduct && (
                        <IconButton
                            size='small'
                            onClick={() => selectProduct({ id })}
                            sx={{ position: 'absolute', right: 2, top: 5 }}
                        >
                            <CloseIcon />
                        </IconButton>
                    )}
                </Stack>
                <Typography variant='body2' color={'primary.mui.inactive'}>
                    {typeLabel}
                </Typography>
                <Stack direction='row' spacing={1} mb={2} useFlexGap flexWrap='wrap'>
                    {chips || []}
                </Stack>
            </Stack>
            <Stack direction='column'>
                <Box sx={{ width: '100%', pt: 1 }}>
                    <Divider />
                </Box>
                <Stack direction='row' justifyContent='space-between' alignItems='center' px={2} py={1}>
                    <Button
                        variant='text'
                        size='small'
                        color='primary'
                        endIcon={<ArrowForwardIosIcon size='small' />}
                        onClick={() => {
                            navigate(
                                reverse('productExploration.detail', {
                                    params: { type: catalogType, slug: slugLabel, productId: id },
                                })
                            );
                        }}
                    >
                        {_('Ver detalle')}
                    </Button>
                </Stack>
            </Stack>
        </Stack>
    );
};

const ProductComparator = ({
    selectedProducts,
    selectProduct,
    resetAndSelectMultipleProducts,
    selectedType,
    resetSelectedProducts,
}: {
    selectedProducts: Set<number>;
    selectProduct: ReturnType<typeof useProductComparator>['selectProduct'];
    resetAndSelectMultipleProducts: ReturnType<typeof useProductComparator>['resetAndSelectMultipleProducts'];
    selectedType: ReturnType<typeof useProductComparator>['selectedType'];
    resetSelectedProducts: ReturnType<typeof useProductComparator>['resetSelectedProducts'];
}) => {
    const units = useUnitProvider();
    const catalogProductsByIdQuery = useCatalogProductsById({
        ids: Array.from(selectedProducts),
        crop: selectedType || 'corn',
    });

    const products = parseProductForProductCard(catalogProductsByIdQuery.data || [], units);
    const navigate = useNavigate();
    const { open, setOpen, setClose } = useToggle();
    const { type: crop } = useParams();
    useEffect(() => {
        resetSelectedProducts();
    }, [crop]);

    const isOpen = useMemo(() => {
        return selectedProducts.size > 0 && crop !== 'crop_protection';
    }, [selectedProducts, crop]);
    useEffect(() => {
        if (isOpen) {
            mpTrackEvent('Product Compare flow:Started', {});
        }
    }, [isOpen]);

    if (!isOpen) return null;
    return (
        <>
            <Box pb={'350px'} />
            <Stack
                sx={{
                    background: 'rgba(16, 56, 79, 0.9)',
                    position: 'fixed',
                    width: { xs: '100%', sm: 'fill-available' },
                    bottom: 0,
                    left: { xs: 0, sm: 80 },
                    zIndex: 1300,
                }}
                px={{ xs: 2, sm: 4 }}
                py={2}
                spacing={1}
            >
                <Stack direction='row' justifyContent='space-between' alignItems='center'>
                    <Typography
                        sx={{
                            color: 'white !important',
                        }}
                        variant='h6'
                    >
                        {_('Seleccioná hasta 4 productos para comparar')}
                    </Typography>
                    <IconButton
                        sx={{
                            color: 'white !important',
                        }}
                        onClick={() => resetSelectedProducts()}
                    >
                        <CloseIcon
                            sx={{
                                width: '16px',
                                height: '16px',
                            }}
                        />
                    </IconButton>
                </Stack>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(4, 1fr)',
                        justifyItems: 'center',
                        gap: 2,
                        overflow: 'scroll',
                        scrollbarWidth: 'none',
                        '&::-webkit-scrollbar': {
                            display: 'none',
                        },
                    }}
                    px={{ sm: 5 }}
                    py={3}
                >
                    {catalogProductsByIdQuery.isValidating ? (
                        // Show loading state for all slots
                        Array.from({ length: 4 }).map((u, index) => (
                            <Stack
                                key={`loading-slot-${index}-${Date.now()}`}
                                spacing={2}
                                p={2}
                                sx={{
                                    position: 'relative',
                                    border: '2px dashed rgba(255, 255, 255, 0.5)',
                                    borderRadius: 2,
                                    width: '100%',
                                    height: '160px',
                                }}
                                alignItems='center'
                                justifyContent='center'
                                textAlign='center'
                            >
                                <Skeleton variant='rectangular' width='100%' height='100%' />
                            </Stack>
                        ))
                    ) : (
                        <>
                            {products.map((el) => (
                                <ProductCard
                                    key={el.id}
                                    {...el}
                                    selectProduct={selectProduct}
                                    sx={{
                                        width: '100%',
                                    }}
                                    chips={el.chips}
                                />
                            ))}
                            {Array.from({ length: 4 - products.length }).map((u, index) => (
                                <Stack
                                    key={`empty-slot-${index}-${Date.now()}`}
                                    spacing={2}
                                    p={2}
                                    sx={{
                                        position: 'relative',
                                        border: '2px dashed white',
                                        borderRadius: 2,
                                        width: '100%',
                                        cursor: 'pointer',
                                    }}
                                    alignItems='center'
                                    justifyContent='center'
                                    textAlign='center'
                                    onClick={setOpen}
                                >
                                    <Typography variant='subtitle2' sx={{ color: 'white !important', fontWeight: 700 }}>
                                        + {_('Agregar un híbrido')}
                                    </Typography>
                                </Stack>
                            ))}
                        </>
                    )}
                </Box>
                <Stack direction='row' spacing={3} ml={{ sm: 'auto' }} justifyContent='end'>
                    <Button
                        sx={{
                            color: 'white !important',
                        }}
                        onClick={() => resetSelectedProducts()}
                    >
                        {_('Borrar')}
                    </Button>
                    <Button
                        variant='contained'
                        sx={{
                            color: 'white !important',
                        }}
                        disabled={selectedProducts.size < 2 || catalogProductsByIdQuery.isValidating}
                        onClick={() => {
                            navigate(
                                reverse('productExploration.comparator', {
                                    params: { productIds: Array.from(selectedProducts), catalogType: selectedType },
                                })
                            );
                        }}
                    >
                        {_('Comparar')}
                    </Button>
                </Stack>
            </Stack>
            <ProductComparatorModal
                open={open}
                setClose={setClose}
                selectedProducts={selectedProducts}
                resetAndSelectMultipleProducts={resetAndSelectMultipleProducts}
            />
        </>
    );
};

export default ProductComparator;

export const ProductComparatorModal = ({
    open,
    setClose,
    selectedProducts,
    resetAndSelectMultipleProducts,
}: {
    open: boolean;
    setClose: () => void;
    selectedProducts: Set<number>;
    resetAndSelectMultipleProducts: ReturnType<typeof useProductComparator>['resetAndSelectMultipleProducts'];
}) => {
    const units = useUnitProvider();
    const catalogZonesQuery = useCatalogZones({ page: 1, pageSize: 10000, type: 'catalog' });
    const country = useSelector(getSessionCountry);
    const { type: crop, slug: brand } = useParams();
    const catalogZones = useMemo(
        () =>
            catalogZonesQuery.data?.results?.filter(
                (z) => z.countryId === country?.id && z.crop === (crop || 'corn')
            ) || [],
        [catalogZonesQuery.data?.results, country?.id, crop]
    );
    const [selectedZone, setSelectedZone] = useState<string | undefined>(undefined);
    const [preSelectedProducts, setPreSelectedProducts] = useState<Set<number>>(new Set(selectedProducts));
    const preSelectionMaxSize = 4;

    useEffect(() => {
        resetAndSelectMultipleProducts(Array.from(selectedProducts));
    }, [open]);

    useEffect(() => {
        setPreSelectedProducts(new Set(selectedProducts));
    }, [selectedProducts]);

    const allZonesProductsQuery = useAllCatalogProducts(
        {
            brand: brand || 'dekalb',
            crop: crop || 'corn',
            country: country?.code || 'AR',
            page: 1,
            pageSize: 10000,
        },
        { enabled: true }
    );

    const productsQuery = useAllCatalogProducts(
        {
            brand: brand || 'dekalb',
            crop: crop || 'corn',
            country: country?.code || 'AR',
            zone: selectedZone ? Number.parseInt(selectedZone) : undefined,
            page: 1,
            pageSize: 10000,
        },
        { enabled: true }
    );

    const filteredProducts = useMemo(
        () =>
            parseProductForProductCard(
                (productsQuery.data?.results || [])
                    .filter((p) => Boolean(_(p.catalogZone)))
                    .map((p) => ({ ...p, catalogType: 'corn', slugLabel: p.brand.name.toLowerCase() })),
                units
            ),
        [productsQuery.data?.results]
    );

    return (
        <BayerModal
            variant='children'
            title={_('Busca un híbrido para comparar')}
            handleClick={() => {
                resetAndSelectMultipleProducts(Array.from(preSelectedProducts));
                setClose();
                setPreSelectedProducts(new Set());
            }}
            open={open}
            setClose={setClose}
            buttonText={_('Agregar a la comparación')}
            disabled={preSelectedProducts.size === 0}
            containerChildrenSx={{ width: '100%', px: 3 }}
        >
            <Stack direction='column' spacing={2}>
                <FormGroup>
                    <Stack direction='row' spacing={1}>
                        {catalogZones.map((z) => (
                            <FormControlLabel
                                key={z.id}
                                control={
                                    <Radio
                                        onChange={() => {
                                            if (selectedZone === z.id) setSelectedZone(undefined);
                                            else setSelectedZone(z.id);
                                        }}
                                        checked={selectedZone === z.id}
                                    />
                                }
                                label={
                                    <Typography color='primary' variant='body2'>
                                        {_('Zona')} {_(z.name)}
                                    </Typography>
                                }
                                labelPlacement='end'
                            />
                        ))}
                    </Stack>
                </FormGroup>
                <ProductSelectorAutocomplete
                    setPreSelectedProducts={setPreSelectedProducts}
                    filteredProducts={filteredProducts}
                    preSelectedProducts={preSelectedProducts}
                    preSelectionMaxSize={preSelectionMaxSize}
                />
                <Typography variant='body2' color='primary.mui.inactive'>
                    {_('Podés seleccionar hasta {{max}} productos para comparar', { max: preSelectionMaxSize })}
                </Typography>
                {preSelectedProducts.size > 0 && (
                    <Stack direction='row' spacing={1}>
                        {Array.from(preSelectedProducts).map((productId) => {
                            const product = allZonesProductsQuery.data?.results?.find((p) => p.id === productId);
                            return (
                                <Chip
                                    key={productId}
                                    label={product?.catalogName || product?.name}
                                    sx={{
                                        background: colors.themes[settings.app.theme].mui.primary.infoBackground,
                                    }}
                                />
                            );
                        })}
                    </Stack>
                )}
                <FormControl sx={{ maxHeight: '150px', overflow: 'scroll', px: 1 }}>
                    <RadioGroup>
                        {productsQuery.isValidating ? (
                            <Stack direction='column' spacing={1}>
                                {Array.from({ length: 5 }).map((u, index) => (
                                    <Skeleton
                                        key={`loading-${index}-${Date.now()}`}
                                        variant='rectangular'
                                        width={100}
                                        height={20}
                                    />
                                ))}
                            </Stack>
                        ) : (
                            filteredProducts.map((p) => (
                                <FormControlLabel
                                    key={p.id}
                                    value={p.id}
                                    checked={preSelectedProducts.has(p.id)}
                                    control={<Checkbox color='secondary' />}
                                    label={
                                        <Stack direction='row' spacing={1} alignItems='center'>
                                            <Typography variant='subtitle2'>{p.catalogName || p.name}</Typography>
                                            {p.chips?.map((chip) => {
                                                return chip;
                                            }) || []}
                                        </Stack>
                                    }
                                    onClick={() => {
                                        const newSet = new Set(preSelectedProducts);
                                        if (newSet.has(p.id)) {
                                            newSet.delete(p.id);
                                        } else {
                                            newSet.add(p.id);
                                        }
                                        setPreSelectedProducts(newSet);
                                    }}
                                    disabled={
                                        preSelectedProducts.size >= preSelectionMaxSize &&
                                        !preSelectedProducts.has(p.id)
                                    }
                                />
                            ))
                        )}
                    </RadioGroup>
                </FormControl>
            </Stack>
        </BayerModal>
    );
};
